// Sunny Day sticker primitives — see design_handoff_redesign/README.md.
// Build the new screens by composing these instead of recreating the
// outline + offset-shadow boilerplate every time.
import { SUNNY } from "../theme/sunny";

// Card / surface with the signature ink outline + hard-offset shadow.
// Apply slight rotation (-1.5° to +1°) on cards/badges when the layout
// allows — that's part of the sticker feel.
export function Sticker({
  as: Component = "div",
  background = SUNNY.paper,
  shadow = SUNNY.shadowCard,
  radius = SUNNY.radiusCard,
  rotate = 0,
  style,
  children,
  ...rest
}) {
  return (
    <Component
      style={{
        background,
        color: SUNNY.ink,
        border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
        borderRadius: radius,
        boxShadow: shadow,
        transform: rotate ? `rotate(${rotate}deg)` : undefined,
        fontFamily: SUNNY.fontBody,
        ...style,
      }}
      {...rest}
    >
      {children}
    </Component>
  );
}

// Adventure patch — uppercase chunky label, optional sub, optional emoji.
// Use for distance / sun-window / feature-count style metadata.
export function Patch({ color = SUNNY.grass, text, sub, rotate = 0, icon, style, onClick, title }) {
  const clickable = typeof onClick === "function";
  return (
    <div
      onClick={onClick}
      title={title}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(e); } } : undefined}
      style={{
        background: color,
        color: "#fff",
        border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
        boxShadow: SUNNY.shadowButton,
        borderRadius: 18,
        padding: "10px 12px",
        transform: rotate ? `rotate(${rotate}deg)` : undefined,
        fontFamily: SUNNY.fontBody,
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        minHeight: 44,
        cursor: clickable ? "pointer" : undefined,
        ...style,
      }}
    >
      {icon}
      <div style={{ lineHeight: 1 }}>
        <div style={{ fontWeight: 900, fontSize: 14, letterSpacing: 0.3, textTransform: "uppercase" }}>{text}</div>
        {sub && <div style={{ fontSize: 11, opacity: 0.92, marginTop: 3 }}>{sub}</div>}
      </div>
    </div>
  );
}

// Status pill — small uppercase chip used for sun/fog/closed indicators.
// `variant` picks the color scheme; pass `text` and an optional emoji prefix
// directly in the children for the inline icon.
export function StatusPill({ variant = "sun", style, children }) {
  const variants = {
    sun:    { background: SUNNY.sun,   color: SUNNY.ink },
    fog:    { background: SUNNY.sky,   color: "#fff" },
    closed: { background: SUNNY.ink,   color: "#fff" },
    info:   { background: SUNNY.paper, color: SUNNY.ink },
  };
  const v = variants[variant] || variants.sun;
  return (
    <div
      style={{
        background: v.background,
        color: v.color,
        padding: "4px 10px",
        borderRadius: SUNNY.radiusPill,
        fontSize: 10,
        fontWeight: 900,
        letterSpacing: 0.8,
        border: `2px solid ${SUNNY.ink}`,
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// Score stamp — circular badge with a big number and a micro-label, rotated
// for sticker feel. Used in card top-right corners (e.g. "92 SUN·FIT").
export function ScoreStamp({ value, label = "SUN·FIT", color = SUNNY.sun, style }) {
  return (
    <div
      style={{
        background: color,
        color: SUNNY.ink,
        border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
        borderRadius: "50%",
        width: 48,
        height: 48,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: SUNNY.fontDisplay,
        fontWeight: 700,
        boxShadow: SUNNY.shadowSticker,
        transform: "rotate(8deg)",
        ...style,
      }}
    >
      <div style={{ fontSize: 16, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 7, fontWeight: 900, letterSpacing: 0.5, marginTop: 2 }}>{label}</div>
    </div>
  );
}

// Weather sticker — marigold oval, ink outline, hard shadow, slight tilt.
// Big temperature in Fraunces; condition + sun-window caption underneath.
// Click handler on the wrapper preserves the existing 3-tap-to-secret-modal
// behavior; consumers wire `onTap` if they need it.
export function WeatherSticker({ temp, conditionText, captionRight, onTap, glyph, compact = false, style }) {
  // `compact` shrinks the sticker for the map-first home where vertical
  // space is precious — single-line, smaller temp, no caption.
  return (
    <div
      onClick={onTap}
      style={{
        background: SUNNY.sun, color: SUNNY.ink,
        borderRadius: compact ? 22 : 28, padding: compact ? "6px 12px" : "12px 16px",
        border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
        boxShadow: `4px 5px 0 ${SUNNY.ink}`,
        display: "inline-flex", alignItems: "center", gap: compact ? 8 : 12,
        transform: "rotate(-1.2deg)",
        cursor: onTap ? "pointer" : "default",
        userSelect: "none",
        ...style,
      }}
    >
      {glyph || <SunGlyph size={compact ? 28 : 48} />}
      <div style={{ lineHeight: 1.05 }}>
        <div style={{ fontFamily: SUNNY.fontDisplay, fontSize: compact ? 18 : 28, fontWeight: 700, lineHeight: 1, letterSpacing: -0.5 }}>
          {temp}
        </div>
        {conditionText && (
          <div style={{ fontFamily: SUNNY.fontBody, fontSize: compact ? 9 : 11, fontWeight: 800, marginTop: compact ? 1 : 4, opacity: 0.85, textTransform: "uppercase", letterSpacing: 0.5 }}>
            {conditionText}
          </div>
        )}
        {captionRight && !compact && (
          <div style={{ fontSize: 10, fontWeight: 700, marginTop: 2, opacity: 0.7 }}>
            {captionRight}
          </div>
        )}
      </div>
    </div>
  );
}

// Sun arc bar — the signature element of the redesign. Cream pill,
// gradient (sky → sun → sunDeep) clipped to "% of day elapsed", vertical
// ink line at the NOW position with floating NOW badge, sunrise/noon/
// sunset tick labels below.
//
// Props:
//   sunrise, sunset: Date objects from getSunTimes()
//   now: Date (defaults to new Date())
//   timeFmt: optional Date → string formatter; defaults to "h:MM am/pm"
export function SunArcBar({ sunrise, sunset, now = new Date(), timeFmt, compact = false }) {
  if (!sunrise || !sunset) return null;
  const fmt = timeFmt || ((d) => {
    let h = d.getHours();
    const m = String(d.getMinutes()).padStart(2, "0");
    const ampm = h >= 12 ? "pm" : "am";
    h = h % 12 || 12;
    return `${h}:${m} ${ampm}`;
  });

  const dayLen = sunset.getTime() - sunrise.getTime();
  const elapsed = now.getTime() - sunrise.getTime();
  // Out-of-day flag — when "now" is outside [sunrise, sunset], pinning the
  // NOW marker to the clamp endpoint reads as "the time is exactly sunset"
  // which is misleading. Hide the marker entirely and dim the bar instead.
  const isBeforeSunrise = now.getTime() < sunrise.getTime();
  const isAfterSunset = now.getTime() > sunset.getTime();
  const isNight = isBeforeSunrise || isAfterSunset;
  const pct = Math.max(0, Math.min(1, dayLen > 0 ? elapsed / dayLen : 0));
  const pctStr = (pct * 100).toFixed(1);

  return (
    <div style={{ width: "100%" }}>
      {/* Outer wrapper — no overflow:hidden so the NOW badge can float
          above the bar. We need extra top padding to make room for it. */}
      <div style={{
        position: "relative",
        paddingTop: 10,
      }}>
        {/* Inner bar — overflow:hidden + borderRadius keep the gradient
            clipped to the rounded pill shape. Dimmed at night to reinforce
            that the day's window is over. */}
        <div style={{
          background: SUNNY.cream,
          border: `${SUNNY.borderWidth}px solid ${SUNNY.ink}`,
          borderRadius: SUNNY.radiusPill,
          height: compact ? 16 : 26,
          position: "relative",
          overflow: "hidden",
          boxShadow: SUNNY.shadowButton,
          opacity: isNight ? 0.45 : 1,
        }}>
          <div style={{
            position: "absolute", inset: 0,
            background: `linear-gradient(90deg, ${SUNNY.sky} 0%, ${SUNNY.sun} 30%, ${SUNNY.sun} 75%, ${SUNNY.sunDeep} 100%)`,
            clipPath: `polygon(0 0, ${pctStr}% 0, ${pctStr}% 100%, 0 100%)`,
          }} />
        </div>
        {/* NOW marker — only render during the daylight window. At night
            the marker would be pinned to the sunrise or sunset edge, which
            wrongly reads as "the current time is sunset/sunrise". */}
        {!isNight && (
          <>
            {/* Vertical NOW line */}
            <div style={{
              position: "absolute", left: `${pctStr}%`, top: 6, height: (compact ? 16 : 26) + 8,
              width: 3, background: SUNNY.ink,
              transform: "translateX(-50%)",
            }} />
            {/* NOW badge floating above the bar */}
            <div style={{
              position: "absolute", left: `${pctStr}%`, top: 0,
              transform: "translateX(-50%)",
              background: SUNNY.ink, color: SUNNY.cream,
              fontFamily: SUNNY.fontBody, fontSize: 9, fontWeight: 900,
              padding: "2px 6px", borderRadius: 6, letterSpacing: 0.5,
              whiteSpace: "nowrap", pointerEvents: "none",
            }}>NOW</div>
          </>
        )}
        {/* Night badge — explicit "after sunset" / "before sunrise" floating
            above the right or left edge of the bar so the user knows where
            in the day cycle they are. */}
        {isNight && !compact && (
          <div style={{
            position: "absolute",
            left: isAfterSunset ? "auto" : 0,
            right: isAfterSunset ? 0 : "auto",
            top: 0,
            background: SUNNY.ink, color: SUNNY.cream,
            fontFamily: SUNNY.fontBody, fontSize: 9, fontWeight: 900,
            padding: "2px 6px", borderRadius: 6, letterSpacing: 0.5,
            whiteSpace: "nowrap", pointerEvents: "none",
          }}>{isAfterSunset ? "🌙 AFTER SUNSET" : "🌙 BEFORE SUNRISE"}</div>
        )}
      </div>
      {!compact && (
        <div style={{
          display: "flex", justifyContent: "space-between",
          fontFamily: SUNNY.fontBody, fontSize: 10, fontWeight: 800,
          color: SUNNY.inkSoft, opacity: 0.7, marginTop: 6, padding: "0 2px",
        }}>
          <span>{fmt(sunrise).toUpperCase()}</span>
          <span>NOON</span>
          <span>{fmt(sunset).toUpperCase()}</span>
        </div>
      )}
    </div>
  );
}

// Sun-with-rays glyph — used in the weather sticker and elsewhere we need
// a clean iconographic sun. SVG so it scales without artifacts.
export function SunGlyph({ size = 56, strokeColor = SUNNY.ink, fillColor = "#fff" }) {
  const half = size / 2;
  const inner = size * 0.32;
  const outer = size * 0.46;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {Array.from({ length: 8 }).map((_, i) => {
        const a = (i * 45) * Math.PI / 180;
        return (
          <line
            key={i}
            x1={half + Math.cos(a) * inner}
            y1={half + Math.sin(a) * inner}
            x2={half + Math.cos(a) * outer}
            y2={half + Math.sin(a) * outer}
            stroke={strokeColor}
            strokeWidth={size * 0.054}
            strokeLinecap="round"
          />
        );
      })}
      <circle cx={half} cy={half} r={size * 0.23} fill={fillColor} stroke={strokeColor} strokeWidth={size * 0.054} />
    </svg>
  );
}
